import { api } from './api';
import { Payment, PaymentMethod } from '../types';

export interface CreatePaymentRequest {
  invoiceId: string;
  amount: number; // cents
  paymentDate: string; // ISO date (YYYY-MM-DD)
  paymentMethod: PaymentMethod;
  reference?: string;
  notes?: string;
}

export interface UpdatePaymentRequest {
  amount?: number;
  paymentDate?: string;
  paymentMethod?: PaymentMethod;
  reference?: string;
  notes?: string;
}

export interface CreatePaymentResponse {
  payment: Payment;
  overpaymentCredit?: number;
}

export interface PaymentWithRunningBalance {
  type: 'payment' | 'credit';
  id: string;
  paymentId: string; // Alias for id for backwards compatibility
  date: string;
  paymentDate: string; // Alias for date
  amount: number;
  runningBalance: number;
  paymentMethod?: PaymentMethod;
  reference?: string;
  notes?: string;
  reason?: string;
  status: 'active' | 'voided';
  createdAt: string;
}

export interface ListPaymentsResponse {
  payments: PaymentWithRunningBalance[];
  invoiceTotal: number;
  currentBalance: number;
}

interface BackendTransaction {
  type: 'payment' | 'credit';
  id: string;
  date: string;
  amount: number;
  runningBalance: number;
  paymentMethod?: string;
  reference?: string;
  notes?: string;
  reason?: string;
  status: 'active' | 'voided';
  createdAt: string;
}

interface BackendListResponse {
  invoiceId: string;
  invoiceTotal: number;
  currentBalance: number;
  transactions: BackendTransaction[];
}

export const paymentsApi = {
  list: async (invoiceId: string): Promise<ListPaymentsResponse> => {
    const response = await api.get<BackendListResponse>(`/payments?invoiceId=${invoiceId}`);
    // Transform backend response to frontend format
    const payments: PaymentWithRunningBalance[] = (response.transactions || []).map(t => ({
      ...t,
      paymentId: t.id,
      paymentDate: t.date,
      paymentMethod: t.paymentMethod as PaymentMethod | undefined,
    }));
    return {
      payments,
      invoiceTotal: response.invoiceTotal,
      currentBalance: response.currentBalance,
    };
  },

  create: async (data: CreatePaymentRequest): Promise<CreatePaymentResponse> => {
    return api.post<CreatePaymentResponse>('/payments', data);
  },

  update: async (id: string, data: UpdatePaymentRequest): Promise<Payment> => {
    return api.put<Payment>(`/payments/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return api.delete<void>(`/payments/${id}`);
  },

  void: async (id: string, reason?: string): Promise<Payment> => {
    return api.post<Payment>(`/payments/${id}/void`, { reason });
  },
};
